import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listProducts, getSettings, fileUrl } from "../lib/api";
import { ProductMarquee } from "../components/ProductMarquee";
import { InquiryForm } from "../components/InquiryForm";
import { GallerySection } from "../components/GallerySection";
import { TestimonialsSection } from "../components/TestimonialsSection";
import { useSettings, AddressLines } from "../lib/settings";
import { useSeo } from "../lib/useSeo";
import { Reveal } from "../components/Reveal";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Zap, Award, MapPin } from "lucide-react";

const DEFAULT_HERO_BG = "https://images.unsplash.com/flagged/photo-1566838616631-f2618f74a6a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsJTIwcm9vZiUyMGhvdXNlfGVufDB8fHx8MTc4MzE2NjY2OHww&ixlib=rb-4.1.0&q=85";

const heroItem = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function Home() {
  useSeo({});
  const [products, setProducts] = useState([]);
  const [speed, setSpeed] = useState(40);
  const [userSpeed, setUserSpeed] = useState(null); // local override
  const { settings } = useSettings();
  const heroBg = fileUrl(settings.hero_bg_url) || DEFAULT_HERO_BG;

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([listProducts(), getSettings()]);
        setProducts(p);
        setSpeed(s.marquee_speed || 40);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const activeSpeed = userSpeed ?? speed;

  return (
    <div>
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative min-h-[70vh] sm:min-h-[85vh] flex items-center"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/60 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-white">
          <motion.div
            className="max-w-2xl"
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.14, delayChildren: 0.1 } } }}
          >
            <motion.div variants={heroItem} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-bold mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              Renewable Energy Solution Provider
            </motion.div>
            <motion.h1 variants={heroItem} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none mb-6">
              Power your home <br />with the <span className="text-primary">Sun.</span>
            </motion.h1>
            <motion.p variants={heroItem} className="text-lg text-white/80 leading-relaxed mb-8 max-w-lg">
              Premium solar panels, inverters, batteries and complete rooftop installations — engineered for
              Maharashtra's climate. Save up to 90% on electricity bills.
            </motion.p>
            <motion.div variants={heroItem} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/products"
                data-testid="hero-explore-btn"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-orange-600 transition-colors font-medium rounded-full px-8 py-3.5 sm:py-3 w-full sm:w-auto"
              >
                Explore Products <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-quote-btn"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md border-2 border-white text-white hover:bg-white hover:text-secondary transition-colors font-medium rounded-full px-8 py-3.5 sm:py-3 w-full sm:w-auto"
              >
                Get Free Quote
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats / Trust */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 grid md:grid-cols-3 gap-3 sm:gap-6">
        {[
          { icon: ShieldCheck, title: "25 Year Warranty", desc: "On solar panels" },
          { icon: Zap, title: "500+ Installations", desc: "Across Vidarbha" },
          { icon: Award, title: "Certified Installers", desc: "MNRE approved" },
        ].map(({ icon: Icon, title, desc }, i) => (
          <Reveal
            key={title}
            delay={i * 0.12}
            data-testid={`trust-${title.toLowerCase().replace(/\s/g, "-")}`}
            className="bg-white border border-border rounded-2xl p-4 sm:p-8 flex md:block items-center gap-4 hover:shadow-md hover:-translate-y-1 transition-[box-shadow,transform] duration-300"
          >
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 md:mb-4">
              <Icon className="w-6 h-6 text-primary" strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-heading text-xl sm:text-2xl font-semibold mb-0.5 sm:mb-1">{title}</div>
              <div className="text-sm text-muted-foreground">{desc}</div>
            </div>
          </Reveal>
        ))}
      </section>

      {/* Marquee section */}
      <section className="py-12 bg-white border-y border-border">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-2">Our Range</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              Featured products
            </h2>
          </div>
        </Reveal>
        <ProductMarquee products={products} speed={activeSpeed} />
      </section>

      {/* Solar projects gallery */}
      <GallerySection />

      {/* Customer testimonials */}
      <TestimonialsSection />

      {/* Address block */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">Visit Us</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-6">
              Come meet us at our shop in Akola.
            </h2>
            <div className="flex items-start gap-4 bg-white border border-border rounded-2xl p-6">
              <MapPin className="w-6 h-6 text-primary shrink-0 mt-0.5" strokeWidth={1.75} />
              <div>
                <div className="font-heading text-xl font-semibold mb-1">{settings.company_name}</div>
                <div className="text-secondary leading-relaxed" data-testid="home-address">
                  <AddressLines text={settings.shop_address} />
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="bg-white border border-border rounded-2xl p-8">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-2">Get in touch</div>
            <h3 className="font-heading text-3xl font-bold tracking-tight mb-6">
              Request a free site visit.
            </h3>
            <InquiryForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
